/** Release 2 C4 — usage-rights helpers shared by the list view, the rights editor, badges and the facet. */
import type { Schemas } from '@/lib/api/types';

export type RightsStatus = Schemas['RightsStatus'];
export type RightsLicense = Schemas['RightsLicense'];
export type ReleaseStatus = Schemas['ReleaseStatus'];
// --- release4/R --- META-3
export type CcVariant = Schemas['CcVariant'];
export const CC_VARIANT_LABEL: Record<CcVariant, string> = { by: 'CC BY', 'by-sa': 'CC BY-SA', 'by-nc': 'CC BY-NC', 'by-nc-sa': 'CC BY-NC-SA', 'by-nd': 'CC BY-ND', 'by-nc-nd': 'CC BY-NC-ND', zero: 'CC0' };
export const CC_VARIANTS = Object.keys(CC_VARIANT_LABEL) as CcVariant[];
/** Embargo: `valid_from` still in the future (card badge, editor summary). */
export function isEmbargoed(validFrom: string | null | undefined, now = Date.now()): boolean {
  if (!validFrom) return false;
  const t = Date.parse(validFrom);
  return !Number.isNaN(t) && t > now;
}
/** Facet bucket values: `RightsStatus` plus release4/R `embargoed`. */
export type RightsBucket = RightsStatus | 'embargoed';

/** Same window as the API's `RIGHTS_EXPIRING_DAYS` (the `rights` facet's `expiring` bucket). */
export const RIGHTS_EXPIRING_DAYS = 30;

export const LICENSE_LABEL: Record<RightsLicense, string> = {
  royalty_free: 'Royalty free',
  rights_managed: 'Rights managed',
  editorial: 'Editorial only',
  creative_commons: 'Creative Commons',
  owned: 'Owned',
  other: 'Other',
};
export const LICENSES = Object.keys(LICENSE_LABEL) as RightsLicense[];

export const RELEASE_LABEL: Record<ReleaseStatus, string> = { not_required: 'Not required', pending: 'Pending', obtained: 'Obtained', missing: 'Missing' };
export const RELEASES = Object.keys(RELEASE_LABEL) as ReleaseStatus[];

export const RIGHTS_STATUS_LABEL: Record<RightsStatus, string> = { none: 'No expiry', valid: 'Valid', expiring: 'Expiring soon', expired: 'Expired' };
/** release4/R: the facet panel's buckets (`embargoed` first — it wins over the expiry buckets server-side). */
export const RIGHTS_BUCKET_LABEL: Record<RightsBucket, string> = { embargoed: 'Embargoed', ...RIGHTS_STATUS_LABEL };

/** Client-side mirror of the API's `rightsStatus` (used to patch caches before the server echo). */
export function rightsStatusOf(expiresAt: string | null | undefined, now = Date.now()): RightsStatus {
  if (!expiresAt) return 'none';
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return 'none';
  if (t <= now) return 'expired';
  return t <= now + RIGHTS_EXPIRING_DAYS * 86400e3 ? 'expiring' : 'valid';
}

/** `<input type="date">` value (local calendar day) ↔ ISO instant at the end of that local day. */
export function expiryToDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
export function dateInputToExpiry(value: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59).toISOString();
}

export const RIGHTS_STATUS_TONE: Record<RightsStatus, 'neutral' | 'ok' | 'warn' | 'danger'> = { none: 'neutral', valid: 'ok', expiring: 'warn', expired: 'danger' };

// --- facet / search -------------------------------------------------------------------------------
type RightsFilterC = NonNullable<Schemas['SearchFilters']['rights']>;
type SearchRequestC = Schemas['SearchRequest'];

/**
 * The `rights` filter each facet bucket selects. The filter cannot say "not expiring", so `valid` selects every asset
 * with a future expiry (the bucket count excludes the expiring ones).
 */
export const RIGHTS_BUCKET_FILTER: Record<RightsBucket, RightsFilterC> = {
  embargoed: { not_yet_valid: true }, // release4/R
  none: { has_expiry: false },
  valid: { has_expiry: true, expired: false },
  expiring: { expiring_within_days: RIGHTS_EXPIRING_DAYS },
  expired: { expired: true },
};

/** Which bucket a `rights` filter is (null when it is something else, e.g. set by a smart board). */
export function rightsBucketOf(f: RightsFilterC | undefined): RightsBucket | null {
  if (!f) return null;
  const key = JSON.stringify(Object.entries(f).filter(([, v]) => v !== undefined).sort());
  return (Object.keys(RIGHTS_BUCKET_FILTER) as RightsBucket[]).find((s) => JSON.stringify(Object.entries(RIGHTS_BUCKET_FILTER[s]).sort()) === key) ?? null;
}

/** The search page asks for the `rights` facet on top of `ALL_FACETS` (same pattern as A's `withTidyFacet`). */
export function withRightsFacet(r: SearchRequestC | null): SearchRequestC | null {
  if (!r || r.facets?.includes('rights')) return r;
  return { ...r, facets: [...(r.facets ?? []), 'rights'] };
}
